import { getCookieData } from '@repo/common-utils';
import { useToast } from '@repo/ui';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { graphql, useMutation } from 'react-relay';
import { useTimeout } from 'react-use';

import LoadingContent from './LoadingContent';

import { DecisionRequestLoadingMutation } from '@/__generated__/DecisionRequestLoadingMutation.graphql';
import { getIndisSearchRefundData } from '@/components/tax-refund/common/data/indis-search-refund-data';
import { CHECK_DECISION_ACTION_CODE_COOKIE_KEY } from '@/components/tax-refund/common/data/result-refund-data';
import { TAX_REFUND_ERROR } from '@/components/tax-refund/common/error/error';
import NetworkError from '@/components/tax-refund/common/view/ErrorView/NetworkError';
import { DECISION_PATHS } from '@/lib/constants/paths';
import useSaveRefundActionLog from '@/lib/hooks/api/mutation/use-save-refund-action-log';
import useNavigatorOnline from '@/lib/hooks/use-navigator-online';
import { refundTokenAtom } from '@/lib/stores/simple-auth';

interface Props {
  handleShowNetworkDisconnection: (isShow: boolean) => void;
}
type DecisionRequestLoadingProps = Props;

const decisionRequestLoadingMutation = graphql`
  mutation DecisionRequestLoadingMutation($refundId: String!, $refundToken: String!) {
    searchHometaxRefundResultByLogin(refundId: $refundId, refundToken: $refundToken) {
      result
      hometaxRefundResult
      errors {
        code
        message
      }
      taxpayerName
    }
  }
`;

const DecisionRequestLoading: FC<DecisionRequestLoadingProps> = ({ handleShowNetworkDisconnection }) => {
  const { refundId } = getIndisSearchRefundData() ?? {};
  const [readyFn] = useTimeout(3000);
  const isReady = readyFn();
  const router = useRouter();
  const { toast } = useToast();
  const [status, setStatus] = useState('');
  const { commit: saveActionLog } = useSaveRefundActionLog();
  let retryCount = 0;

  const [showNetworkDisconnection, setShowNetworkDisconnection] = useState(false);
  const [disabledButton, setDisabledButton] = useState(false);
  const online = useRef(window?.navigator?.onLine ?? true);

  const refundToken = useAtomValue(refundTokenAtom);

  const [searchHometaxRefundResultByLoginCommit, isSearchHometaxRefundResultByLoginLoading] =
    useMutation<DecisionRequestLoadingMutation>(decisionRequestLoadingMutation);

  const setOnline = (_online: boolean) => {
    online.current = _online;
    setDisabledButton(!_online);
  };
  useNavigatorOnline(setOnline);

  const request = useCallback(() => {
    if (!refundToken || !refundId) return;
    if (isSearchHometaxRefundResultByLoginLoading) return;
    searchHometaxRefundResultByLoginCommit({
      variables: {
        refundId: refundId ?? '',
        refundToken: refundToken ?? ''
      },
      onCompleted: (data) => {
        const { searchHometaxRefundResultByLogin } = data || {};
        const { hometaxRefundResult, errors } = searchHometaxRefundResultByLogin || {};
        const actionCode = router.query.actionCode ?? getCookieData(CHECK_DECISION_ACTION_CODE_COOKIE_KEY);
        if (errors?.code) {
          if (errors.code === TAX_REFUND_ERROR.기존TIN과불일치) {
            setStatus('기존TIN과불일치');
          } else {
            setStatus('기타에러');
          }
        } else if (hometaxRefundResult === 'Accept') {
          setStatus('환급결정내역있음');
        } else if (hometaxRefundResult === 'None') {
          setStatus('환급결정내역없음');
        }

        saveActionLog({
          variables: {
            actionCode,
            refundId,
            actionStatus: errors?.code ? '조회에러' : '조회완료'
          }
        });
      },
      onError: () => {
        if (!online.current) {
          setShowNetworkDisconnection(true);
          handleShowNetworkDisconnection(true);
          return;
        }

        if (retryCount >= 5) {
          router.replace(DECISION_PATHS.에러기타);
          return;
        }

        toast({ title: '네트워크 불안정으로 조회를 다시 시작할게요.', variant: 'warning' });
        setTimeout(() => {
          retryCount += 1;
          request();
        }, 1000);
      }
    });
  }, [isSearchHometaxRefundResultByLoginLoading]);

  useEffect(() => {
    request();
  }, []);

  useEffect(() => {
    if (!isReady) return;
    switch (status) {
      case '기존TIN과불일치':
        router.replace(DECISION_PATHS.에러동일TIN);
        break;
      case '기타에러':
        router.replace(DECISION_PATHS.에러기타);
        break;
      case '환급결정내역있음':
        router.replace(DECISION_PATHS.환급결정내역있음);
        break;
      case '환급결정내역없음':
        router.replace(DECISION_PATHS.환급결정내역없음);
        break;
      default:
        break;
    }
  }, [status, isReady, router]);

  const handleRetry = () => {
    setShowNetworkDisconnection(false);
    handleShowNetworkDisconnection(false);
    request();
  };

  return <>{showNetworkDisconnection ? <NetworkError disabledPrimaryButton={disabledButton} onPrimaryButtonClick={handleRetry} /> : <LoadingContent />}</>;
};

export default DecisionRequestLoading;
